import React from 'react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

import './commentHighlights.scss'

// =============================================================================
// renderTextWithMarks — wrap offset ranges of `text` in clickable <mark>s so a
// comment's anchored span is visible in its source text. Shared by the prompt
// segments and transcript turns. `marks` is [{ start, end, id, active, draft }].
// A `draft` mark is the in-progress selection being commented on — it paints
// like a selection and is non-interactive (no comment to open yet).
// Overlapping ranges are resolved greedily (earlier-starting / longer mark
// wins the shared characters) so no character is ever double-wrapped.
// Returns an array of strings + <mark> elements usable as React children.
// =============================================================================

// Clamp marks to [0, length], drop empties, and order by start (longer first on
// a tie) so the greedy overlap resolution in the loop is deterministic.
const orderMarks = (marks, length) =>
  marks
    .map((mark) => ({
      id: mark.id,
      active: Boolean(mark.active),
      draft: Boolean(mark.draft),
      start: Math.max(0, Math.min(mark.start ?? 0, length)),
      end: Math.max(0, Math.min(mark.end ?? 0, length)),
    }))
    .filter((mark) => mark.end > mark.start)
    .sort((left, right) => left.start - right.start || right.end - left.end)

// Interactive props go through a spread object (same pattern as
// ChangeRequestCard) so the a11y linter doesn't flag role/tabIndex on a span.
const interactivePropsFor = (mark, onMarkClick) => {
  // A draft mark is the live selection, not a saved comment — nothing to open.
  if (isNil(onMarkClick) || mark.draft) {
    return {}
  }
  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onMarkClick(mark.id),
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onMarkClick(mark.id)
      }
    },
  }
}

export const renderTextWithMarks = (text, marks, options = {}) => {
  const {
    className = 'comment-highlight',
    activeClassName = 'comment-highlight--active',
    draftClassName = 'comment-highlight--draft',
    onMarkClick,
  } = options

  if (isNil(text) || isEmpty(text) || isEmpty(marks)) {
    return [text]
  }

  const children = []
  let cursor = 0

  orderMarks(marks, text.length).forEach((mark) => {
    const start = Math.max(mark.start, cursor)
    if (start >= mark.end) {
      return
    }
    if (start > cursor) {
      children.push(text.slice(cursor, start))
    }
    children.push(
      // The highlight look comes from the class, not a <mark> element.
      <span
        key={`mark-${mark.id}-${start}`}
        className={classNames(className, {
          [activeClassName]: mark.active,
          [draftClassName]: mark.draft,
        })}
        data-testid={`comment-mark-${mark.id}`}
        {...interactivePropsFor(mark, onMarkClick)}
      >
        {text.slice(start, mark.end)}
      </span>
    )
    cursor = mark.end
  })

  if (cursor < text.length) {
    children.push(text.slice(cursor))
  }

  return children
}
